import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const useUsuarios = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('usuarios'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createUsuario = useCallback(async (usuarioData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('usuario'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          nombres: usuarioData.Nombres || usuarioData.nombres,
          apellidos: usuarioData.Apellidos || usuarioData.apellidos,
          email: usuarioData.Email || usuarioData.email,
          password: usuarioData.Password || usuarioData.password
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      await fetchUsuarios();
      return result;
    } catch (err) {
      const errorMessage = 'Error al crear usuario';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUsuarios]);

  const updateUsuario = useCallback(async (id, usuarioData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl(`usuario/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          nombres: usuarioData.Nombres || usuarioData.nombres,
          apellidos: usuarioData.Apellidos || usuarioData.apellidos,
          email: usuarioData.Email || usuarioData.email,
          password: usuarioData.Password || usuarioData.password,
          registro: usuarioData.Registro || usuarioData.registro
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      await fetchUsuarios();
      return result;
    } catch (err) {
      const errorMessage = 'Error al actualizar usuario';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUsuarios]);

  const deleteUsuario = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl(`usuario/${id}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      await fetchUsuarios();
      return result;
    } catch (err) {
      const errorMessage = 'Error al eliminar usuario';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUsuarios]);

  useEffect(() => {
    if (immediate) {
      fetchUsuarios();
    }
  }, [immediate, fetchUsuarios]);

  return {
    data,
    loading,
    error,
    refetch: fetchUsuarios,
    create: createUsuario,
    update: updateUsuario,
    delete: deleteUsuario,
    selectedId,
    setSelectedId,
  };
};

export const getUsuarioById = async (id) => {
  return apiRequest(`usuario/${id}`, { method: 'GET' });
};

export const createUsuarioApi = async (usuarioData) => {
  const dataToSend = {
    nombres: usuarioData.Nombres || usuarioData.nombres,
    apellidos: usuarioData.Apellidos || usuarioData.apellidos,
    email: usuarioData.Email || usuarioData.email,
    password: usuarioData.Password || usuarioData.password
  };
  return apiRequest('usuario', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export const updateUsuarioApi = async (id, usuarioData) => {
  const dataToSend = {
    nombres: usuarioData.Nombres || usuarioData.nombres,
    apellidos: usuarioData.Apellidos || usuarioData.apellidos,
    email: usuarioData.Email || usuarioData.email,
    password: usuarioData.Password || usuarioData.password,
    registro: usuarioData.Registro || usuarioData.registro
  };
  return apiRequest(`usuario/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend)
  });
};

export const deleteUsuarioApi = async (id) => {
  return apiRequest(`usuario/${id}`, {
    method: 'DELETE'
  });
};

export const loginUsuario = async (email, password) => {
  return apiRequest('loginUsuario', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
};

export default {
  useUsuarios,
  getUsuarioById,
  createUsuarioApi,
  updateUsuarioApi,
  deleteUsuarioApi,
  loginUsuario
};
